// Allow Hot Module Replacement (HMR) for JS files
// For more details see https://vitejs.dev/guide/api-hmr.html
if (import.meta.hot) {
  import.meta.hot.accept();
}

import './header.scss';

const HEADER_SELECTOR = '[data-header]';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

document.querySelectorAll(HEADER_SELECTOR).forEach((header) => {
  const desktopTriggers = Array.from(header.querySelectorAll('[data-flyout-target]'))
    .filter((el) => el.closest('.header__main-nav'));
  const flyoutPanels = Array.from(header.querySelectorAll('[data-flyout-panel]'));
  const flyoutWrap = header.querySelector('[data-header-flyout-wrap]');
  const flyoutCloseButtons = Array.from(header.querySelectorAll('[data-flyout-close]'));
  const backdrop = header.querySelector('[data-header-backdrop]');
  const mobileToggle = header.querySelector('[data-header-mobile-toggle]');
  const mobilePanel = header.querySelector('[data-header-mobile-panel]');
  const mobileViews = Array.from(header.querySelectorAll('.header__mobile-view[data-mobile-view]'));
  const mobileViewTriggers = Array.from(header.querySelectorAll('.header__mobile-nav [data-mobile-view-target]'));
  const mobileBackTrigger = header.querySelector('.header__mobile-back[data-mobile-view-target]');
  const mobileBreakpoint = window.matchMedia('(max-width: 960px)');
  const mobileBackPrefix = mobileBackTrigger?.getAttribute('data-mobile-back-prefix') ?? 'Back to';
  const mobileBackOverview = mobileBackTrigger?.getAttribute('data-mobile-back-overview') ?? '';
  const mobileViewNames = new Set(mobileViews
    .map((view) => view.getAttribute('data-mobile-view'))
    .filter(Boolean));

  const mobileViewParents = {};
  const mobileViewLabels = {};

  mobileViews.forEach((view) => {
    const currentViewName = view.getAttribute('data-mobile-view');

    if (!currentViewName) {
      return;
    }

    Array.from(view.querySelectorAll('[data-mobile-view-target]')).forEach((trigger) => {
      const childViewName = trigger.getAttribute('data-mobile-view-target');

      if (!childViewName || !mobileViewNames.has(childViewName)) {
        return;
      }

      if (!mobileViewParents[childViewName]) {
        mobileViewParents[childViewName] = currentViewName;
        mobileViewLabels[childViewName] = trigger.querySelector('span')?.textContent.trim() ?? '';
      }
    });
  });

  mobileViewParents.root = null;

  let currentMobileView = 'root';

  let activeFlyout = null;
  let activeFlyoutTrigger = null;

  const getActiveFlyoutPanel = () => flyoutPanels.find((panel) => panel.getAttribute('data-flyout-panel') === activeFlyout);

  const isElementVisible = (element) => {
    if (element.hidden || element.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    return element.getClientRects().length > 0;
  };

  const getFocusableElements = (container) => Array.from(container?.querySelectorAll(FOCUSABLE_SELECTOR) ?? [])
    .filter((element) => isElementVisible(element));

  const getOpenFocusTrapContainer = () => {
    if (mobilePanel && !mobilePanel.hidden) {
      return mobilePanel;
    }

    const activePanel = getActiveFlyoutPanel();

    if (activePanel && !activePanel.hidden) {
      return activePanel;
    }

    return null;
  };

  const trapFocusWithinContainer = (event, container) => {
    if (!container || container.hidden) {
      return false;
    }

    const focusableElements = getFocusableElements(container);

    if (!focusableElements.length) {
      return false;
    }

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;
    const focusIsInsideContainer = activeElement instanceof HTMLElement && container.contains(activeElement);

    if (!focusIsInsideContainer) {
      event.preventDefault();
      (event.shiftKey ? lastFocusableElement : firstFocusableElement).focus();
      return true;
    }

    if (event.shiftKey && activeElement === firstFocusableElement) {
      event.preventDefault();
      lastFocusableElement.focus();
      return true;
    }

    if (!event.shiftKey && activeElement === lastFocusableElement) {
      event.preventDefault();
      firstFocusableElement.focus();
      return true;
    }

    return false;
  };

  const moveFocusToFlyout = () => {
    const activePanel = getActiveFlyoutPanel();

    if (!activePanel || activePanel.hidden) {
      return false;
    }

    const firstFocusableElement = getFocusableElements(activePanel)[0];

    if (!firstFocusableElement) {
      return false;
    }

    firstFocusableElement.focus();

    return true;
  };

  const closeAllSidebars = (restoreFocus = false) => {
    window.altanaSidebar?.closeAll({ restoreFocus });
  };

  const setMobileView = (viewName = 'root') => {
    if (!mobilePanel) {
      return;
    }

    const nextView = mobileViewNames.has(viewName) ? viewName : 'root';
    const isRootView = nextView === 'root';

    mobilePanel.setAttribute('data-mobile-view', nextView);

    mobileViews.forEach((view) => {
      view.hidden = view.getAttribute('data-mobile-view') !== nextView;
    });

    mobileViewTriggers.forEach((trigger) => {
      const isCurrent = !isRootView && trigger.getAttribute('data-mobile-view-target') === nextView;
      trigger.setAttribute('aria-expanded', isCurrent ? 'true' : 'false');
    });
    mobileViews.forEach((view) => {
      if (mobilePanel.getAttribute('data-mobile-view') === view.getAttribute('data-mobile-view')) {
        view.hidden = false;
      } else {
        view.hidden = true;
      }
    });


    if (mobileBackTrigger) {
      mobileBackTrigger.hidden = isRootView;
    }
  };

  const syncBackdrop = () => {
    if (!backdrop) {
      return;
    }

    const shouldShowBackdrop = activeFlyout !== null || (mobilePanel && !mobilePanel.hidden);
    backdrop.hidden = !shouldShowBackdrop;
  };

  const syncPageScrollLock = () => {
    const hasOpenMobileHeader = Array.from(document.querySelectorAll(HEADER_SELECTOR))
      .some((headerElement) => headerElement.classList.contains('is-mobile-open'));

    document.documentElement.classList.toggle('is-scroll-locked', hasOpenMobileHeader);
    document.body.classList.toggle('is-scroll-locked', hasOpenMobileHeader);
  };

  const setOverlayTop = () => {
    const top = `${Math.max(header.getBoundingClientRect().bottom, 0)}px`;
    header.style.setProperty('--header-overlay-top', top);
  };

  const closeFlyout = (restoreFocus = false) => {
    activeFlyout = null;
    header.classList.remove('is-flyout-open');

    desktopTriggers.forEach((trigger) => {
      trigger.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.closest('li')?.classList.remove('is-active');
    });

    flyoutPanels.forEach((panel) => {
      panel.hidden = true;
    });

    if (flyoutWrap) {
      flyoutWrap.hidden = true;
    }

    syncBackdrop();

    if (restoreFocus && activeFlyoutTrigger) {
      activeFlyoutTrigger.focus({ preventScroll: true });
    }

    activeFlyoutTrigger = null;
  };

  const openFlyout = (flyoutId) => {
    if (!flyoutId || mobileBreakpoint.matches) {
      return;
    }

    if (activeFlyout === flyoutId) {
      closeFlyout();
      return;
    }

    activeFlyout = flyoutId;
    activeFlyoutTrigger = desktopTriggers.find((trigger) => trigger.getAttribute('data-flyout-target') === flyoutId) ?? null;
    setOverlayTop();
    closeAllSidebars();
    header.dispatchEvent(new CustomEvent('header:flyoutopen', { bubbles: true }));
    header.classList.add('is-flyout-open');

    if (flyoutWrap) {
      flyoutWrap.hidden = false;
    }

    syncBackdrop();

    desktopTriggers.forEach((trigger) => {
      const isCurrent = trigger.getAttribute('data-flyout-target') === flyoutId;
      trigger.classList.toggle('is-active', isCurrent);
      trigger.setAttribute('aria-expanded', isCurrent ? 'true' : 'false');
      trigger.closest('li')?.classList.toggle('is-active', isCurrent);
    });

    flyoutPanels.forEach((panel) => {
      panel.hidden = panel.getAttribute('data-flyout-panel') !== flyoutId;
    });
  };

  const closeMobilePanel = () => {
    if (!mobilePanel || !mobileToggle) {
      return;
    }

    mobilePanel.hidden = true;
    currentMobileView = 'root';
    setMobileView('root');
    updateBackButton('root');
    mobileToggle.setAttribute('aria-expanded', 'false');
    header.classList.remove('is-mobile-open');
    syncPageScrollLock();
    syncBackdrop();
  };

  const toggleMobilePanel = () => {
    if (!mobilePanel || !mobileToggle) {
      return;
    }

    const shouldOpen = mobilePanel.hidden;

    setOverlayTop();
    mobilePanel.hidden = !shouldOpen;
    currentMobileView = 'root';
    setMobileView('root');
    updateBackButton('root');
    mobileToggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    header.classList.toggle('is-mobile-open', shouldOpen);
    syncPageScrollLock();

    if (shouldOpen) {
      closeFlyout();
      closeAllSidebars();
      header.dispatchEvent(new CustomEvent('header:mobileopen', { bubbles: true }));
    }

    syncBackdrop();
  };

  desktopTriggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      openFlyout(trigger.getAttribute('data-flyout-target'));
    });

    trigger.addEventListener('keydown', (event) => {
      const triggerFlyoutId = trigger.getAttribute('data-flyout-target');
      const isForwardTab = event.key === 'Tab' && !event.shiftKey;

      if (!isForwardTab || !triggerFlyoutId || activeFlyout !== triggerFlyoutId) {
        return;
      }

      if (moveFocusToFlyout()) {
        event.preventDefault();
      }
    });
  });

  const updateBackButton = (viewName) => {
    if (!mobileBackTrigger) {
      return;
    }

    const parentView = mobileViewParents[viewName] || 'root';
    const isRootView = viewName === 'root';

    mobileBackTrigger.hidden = isRootView;
    mobileBackTrigger.setAttribute('data-mobile-view-target', parentView);

    const backButtonText = mobileBackTrigger.querySelector('span');

    if (backButtonText) {
      if (parentView === 'root') {
        backButtonText.textContent = mobileBackOverview;
        return;
      }

      const parentLabel = mobileViewLabels[parentView] ?? '';
      backButtonText.textContent = parentLabel ? `${mobileBackPrefix} ${parentLabel}` : mobileBackOverview;
    }
  };

  const setMobileViewWithHistory = (viewName = 'root') => {
    currentMobileView = viewName;
    setMobileView(currentMobileView);
    updateBackButton(currentMobileView);
  };

  mobileViewTriggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      setMobileViewWithHistory(trigger.getAttribute('data-mobile-view-target'));
    });
  });

  mobileBackTrigger?.addEventListener('click', () => {
    const targetView = mobileBackTrigger.getAttribute('data-mobile-view-target') || 'root';
    setMobileViewWithHistory(targetView);
  });

  flyoutCloseButtons.forEach((button) => {
    button.addEventListener('click', () => closeFlyout(true));
  });

  backdrop?.addEventListener('click', () => {
    closeFlyout(true);
    closeMobilePanel();
    closeAllSidebars(true);
  });
  mobileToggle?.addEventListener('click', toggleMobilePanel);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      const openFocusTrapContainer = getOpenFocusTrapContainer();

      if (openFocusTrapContainer) {
        trapFocusWithinContainer(event, openFocusTrapContainer);
      }
    }

    if (event.key === 'Escape') {
      closeFlyout(true);
      closeMobilePanel();
    }
  });

  document.addEventListener('click', (event) => {
    if (!activeFlyout || mobileBreakpoint.matches) {
      return;
    }

    if (!(event.target instanceof Node)) {
      return;
    }

    const clickedInsideHeader = header.contains(event.target);
    const clickedBackdrop = backdrop?.contains(event.target) ?? false;

    if (!clickedInsideHeader || clickedBackdrop) {
      closeFlyout(true);
    }
  });

  document.addEventListener('sidebar:beforeopen', () => {
    closeFlyout();
    closeMobilePanel();
  });

  const resizeHandler = () => {
    if (!mobileBreakpoint.matches) {
      closeMobilePanel();
    }

    if (activeFlyout || (mobilePanel && !mobilePanel.hidden)) {
      setOverlayTop();
    }
  };

  window.addEventListener('resize', resizeHandler);
  window.addEventListener('scroll', () => {
    if (activeFlyout || (mobilePanel && !mobilePanel.hidden) || window.altanaSidebar?.anyOpen()) {
      setOverlayTop();
    }
  }, { passive: true });

  syncPageScrollLock();
});
